import React, { useEffect, useState } from "react";
import "./DigitalOMR.css";
import { useHistory, useParams } from "react-router-dom";
import OmrApi, { omrError } from "./omrApi";
import Notification from "components/Notification";
import Loader from "components/Loader";
import ArrowBack from "@material-ui/icons/ArrowBack";
import People from "@material-ui/icons/People";
import CheckCircle from "@material-ui/icons/CheckCircle";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import Cancel from "@material-ui/icons/Cancel";
import Search from "@material-ui/icons/Search";
import Assessment from "@material-ui/icons/Assessment";
import GetApp from "@material-ui/icons/GetApp";

// Unwrap a Laravel-style response: { data: <payload> } or the payload itself.
const unwrap = (resp) => {
  const d = resp && resp.data;
  if (d && Object.prototype.hasOwnProperty.call(d, "data")) return d.data;
  return d;
};

// Read the first present value among dotted-path keys.
const pick = (obj, keys, fallback) => {
  if (!obj) return fallback;
  for (let i = 0; i < keys.length; i++) {
    const v = keys[i]
      .split(".")
      .reduce((o, p) => (o == null ? o : o[p]), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

// Find the first array under any of the candidate keys (or the payload itself).
const listFrom = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (let i = 0; i < keys.length; i++) {
    const v = payload[keys[i]];
    if (Array.isArray(v)) return v;
    if (v && Array.isArray(v.data)) return v.data; // nested paginator
  }
  return [];
};

// ---- field-name candidate lists (backend shape is built defensively) ------
const K = {
  studentId: ["student_id", "user_id", "student.id", "user.id", "id"],
  name: [
    "student_name",
    "name",
    "student.name",
    "student.full_name",
    "student.student_name",
    "user.name",
    "user.full_name",
  ],
  marks: ["marks", "total_marks", "score", "obtained_marks", "marks_obtained"],
  percentage: ["percentage", "percent", "percentage_score", "score_percent"],
  correct: ["correct", "correct_count", "correct_answers", "right", "correct_answers_count"],
  incorrect: ["incorrect", "wrong", "incorrect_count", "wrong_count", "wrong_answers"],
  unanswered: ["unanswered", "unattempted", "skipped", "not_answered", "blank"],
  status: ["status", "attempt_status"],
  submittedAt: ["submitted_at", "completed_at", "finished_at", "updated_at"],
  rank: ["rank", "position"],
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
};
const fmtNum = (v) => (v === null || v === undefined || v === "" ? "—" : v);

// submitted / in-progress badge, normalised from a free-text status.
const StatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("submit") || s.includes("complete") || s.includes("evaluat")) {
    return <span className="domr-status published">Submitted</span>;
  }
  if (s.includes("progress") || s.includes("start") || s === "pending") {
    return <span className="domr-status draft">In Progress</span>;
  }
  return <span className="domr-status draft">{status || "—"}</span>;
};

export default function StudentReport() {
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [paper, setPaper] = useState(null);
  const [counts, setCounts] = useState({ total: null, submitted: null, inProgress: null });
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [notif, setNotif] = useState(null);

  const notify = (type, message) => setNotif({ type, message, id: Date.now() });

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([
      OmrApi.getTestPaper(id).catch(() => null),
      OmrApi.getSubmissions(id).catch(() => null),
      OmrApi.getRanking(id).catch(() => null),
    ])
      .then(([paperResp, subResp, rankResp]) => {
        if (!alive) return;

        if (paperResp) setPaper(unwrap(paperResp));

        const sub = subResp ? unwrap(subResp) : null;
        const rank = rankResp ? unwrap(rankResp) : null;

        const subList = listFrom(sub, [
          "results",
          "submissions",
          "students",
          "attempts",
          "data",
        ]);
        const rankList = listFrom(rank, ["ranking", "rankings", "results", "data"]);

        // index submissions by student id so a ranking row can borrow its
        // per-question breakdown / status when ranking omits them.
        const subById = {};
        subList.forEach((r) => {
          const sid = pick(r, K.studentId, null);
          if (sid != null) subById[String(sid)] = r;
        });

        const source = rankList.length ? rankList : subList;
        const built = source.map((primary, idx) => {
          const sid = pick(primary, K.studentId, null);
          const extra = sid != null ? subById[String(sid)] : null;
          // read from primary, then fall back to the matched submission row
          const g = (keys, fb) => {
            const v = pick(primary, keys, undefined);
            if (v !== undefined) return v;
            return extra ? pick(extra, keys, fb) : fb;
          };
          const name =
            pick(primary, K.name, null) ||
            (extra ? pick(extra, K.name, null) : null) ||
            (sid != null ? `Student #${sid}` : "—");
          return {
            key: sid != null ? String(sid) : `row-${idx}`,
            rank: pick(primary, K.rank, idx + 1),
            name,
            marks: g(K.marks, null),
            percentage: g(K.percentage, null),
            correct: g(K.correct, null),
            incorrect: g(K.incorrect, null),
            unanswered: g(K.unanswered, null),
            status: g(K.status, ""),
            submittedAt: g(K.submittedAt, null),
          };
        });

        setRows(built);

        // counts: prefer explicit summary fields, fall back to derivations.
        const total =
          pick(sub, [
            "total",
            "total_students",
            "total_registered",
            "registered",
            "counts.total",
            "summary.total",
            "stats.total",
          ], null) ?? (built.length || null);
        const submitted = pick(sub, [
          "submitted",
          "submitted_count",
          "counts.submitted",
          "summary.submitted",
          "stats.submitted",
        ], null);
        const inProgress = pick(sub, [
          "in_progress",
          "inprogress",
          "in_progress_count",
          "counts.in_progress",
          "summary.in_progress",
          "stats.in_progress",
        ], null);

        setCounts({
          total,
          submitted:
            submitted ??
            built.filter((r) => {
              const s = String(r.status || "").toLowerCase();
              return s.includes("submit") || s.includes("complete");
            }).length,
          inProgress:
            inProgress ??
            built.filter((r) =>
              String(r.status || "").toLowerCase().includes("progress"),
            ).length,
        });

        if (!paperResp && !subResp && !rankResp) {
          notify("error", "Failed to load the student report.");
        }
      })
      .catch((err) => notify("error", omrError(err, "Failed to load the report.")))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const subjectName = (p) => {
    if (!p) return "";
    const s = p.subject;
    if (s && typeof s === "object") return s.name || "";
    return s || p.subject_name || "";
  };

  const filtered = rows.filter((r) =>
    r.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const notAttempted =
    counts.total != null
      ? Math.max(
          0,
          counts.total - (counts.submitted || 0) - (counts.inProgress || 0),
        )
      : null;

  const exportCsv = () => {
    if (!rows.length) {
      notify("error", "Nothing to export yet.");
      return;
    }
    const head = [
      "Rank",
      "Student",
      "Marks",
      "Percentage",
      "Correct",
      "Incorrect",
      "Unanswered",
      "Status",
      "Submitted At",
    ];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = rows.map((r) =>
      [
        r.rank,
        r.name,
        fmtNum(r.marks),
        r.percentage == null ? "" : r.percentage,
        fmtNum(r.correct),
        fmtNum(r.incorrect),
        fmtNum(r.unanswered),
        r.status || "",
        r.submittedAt ? fmtDate(r.submittedAt) : "",
      ]
        .map(esc)
        .join(","),
    );
    const csv = [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${(paper && paper.code) || id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="domr-page">
      {notif && (
        <Notification
          key={notif.id}
          type={notif.type}
          title={notif.message}
          duration={3500}
        />
      )}

      <button
        type="button"
        className="domr-back"
        onClick={() => history.push("/admin/digital-omr")}
      >
        <ArrowBack style={{ fontSize: 18 }} /> Back to Dashboard
      </button>

      <h1 className="domr-page-title">Student Report</h1>
      <p className="domr-page-sub">
        {paper ? (
          <>
            {paper.name}
            {paper.code ? (
              <span className="domr-code-pill" style={{ marginLeft: 10 }}>
                {paper.code}
              </span>
            ) : null}
            {subjectName(paper) ? ` · ${subjectName(paper)}` : ""}
          </>
        ) : (
          "Registered students and their performance for this test paper."
        )}
      </p>

      {loading ? (
        <div className="domr-card" style={{ textAlign: "center", padding: 60 }}>
          <Loader />
        </div>
      ) : (
        <>
          {/* ---- STAT CARDS ------------------------------------------------- */}
          <div className="domr-stats">
            <div className="domr-stat">
              <span className="domr-stat-ico indigo">
                <People />
              </span>
              <div>
                <div className="domr-stat-val">{fmtNum(counts.total)}</div>
                <div className="domr-stat-lbl">Total Students</div>
              </div>
            </div>
            <div className="domr-stat">
              <span className="domr-stat-ico green">
                <CheckCircle />
              </span>
              <div>
                <div className="domr-stat-val">{fmtNum(counts.submitted)}</div>
                <div className="domr-stat-lbl">Submitted</div>
              </div>
            </div>
            <div className="domr-stat">
              <span className="domr-stat-ico amber">
                <HourglassEmpty />
              </span>
              <div>
                <div className="domr-stat-val">{fmtNum(counts.inProgress)}</div>
                <div className="domr-stat-lbl">In Progress</div>
              </div>
            </div>
            <div className="domr-stat">
              <span className="domr-stat-ico red">
                <Cancel />
              </span>
              <div>
                <div className="domr-stat-val">{fmtNum(notAttempted)}</div>
                <div className="domr-stat-lbl">Not Attempted</div>
              </div>
            </div>
          </div>

          {/* ---- TOOLBAR ---------------------------------------------------- */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "flex-end",
              margin: "4px 0 16px",
            }}
          >
            <div className="domr-field" style={{ position: "relative" }}>
              <label>Search student</label>
              <input
                className="domr-input"
                style={{ height: 40, minWidth: 260, paddingRight: 38 }}
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Search
                style={{
                  position: "absolute",
                  right: 12,
                  bottom: 10,
                  color: "#b3b7c7",
                  fontSize: 20,
                }}
              />
            </div>
            <button
              className="domr-btn domr-btn-ghost"
              style={{ marginLeft: "auto" }}
              onClick={exportCsv}
            >
              <GetApp style={{ fontSize: 18 }} /> Export CSV
            </button>
          </div>

          {/* ---- RANKING TABLE ---------------------------------------------- */}
          <div className="domr-table-card">
            <div className="domr-card-head" style={{ padding: "18px 18px 0", marginBottom: 0 }}>
              <span className="domr-chip">
                <Assessment />
              </span>
              <h3>Ranking &amp; Results</h3>
            </div>
            <table className="domr-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Student</th>
                  <th>Marks</th>
                  <th>%</th>
                  <th>Correct</th>
                  <th>Incorrect</th>
                  <th>Unanswered</th>
                  <th>Status</th>
                  <th>Submitted At</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="domr-empty">
                      {rows.length === 0
                        ? "No submissions yet for this paper."
                        : "No students match your search."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <span className={"domr-rank" + (r.rank <= 3 ? " top" : "")}>
                          {r.rank}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{r.name}</td>
                      <td>{fmtNum(r.marks)}</td>
                      <td>
                        {r.percentage == null
                          ? "—"
                          : `${Math.round(Number(r.percentage) * 100) / 100}%`}
                      </td>
                      <td style={{ color: "#16a34a", fontWeight: 600 }}>
                        {fmtNum(r.correct)}
                      </td>
                      <td style={{ color: "#ef4444", fontWeight: 600 }}>
                        {fmtNum(r.incorrect)}
                      </td>
                      <td style={{ color: "#8a90a6", fontWeight: 600 }}>
                        {fmtNum(r.unanswered)}
                      </td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {fmtDate(r.submittedAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
